/**
 * GoodGuardian — Medical Records
 *
 * Tracks doctor visits, diagnoses, medications, and access-denied incidents
 * per child. HIPAA-aware: every record view is audit-logged on the server.
 *
 * Data is fetched per-child on demand (not pre-loaded in global context)
 * because records are PHI and loaded lazily behind the HIPAA consent gate.
 */
import { useState, useMemo, useCallback, useEffect } from 'react'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { data as api } from '@/utils/auth'
import { printMedicalRecords } from '@/utils/printUtils'
import { todayYMD } from '@/utils/dateUtils'

// ── Record type config ─────────────────────────────────────────────────────────
const RECORD_TYPES = [
  { value: 'visit',       label: 'Doctor Visit',      emoji: '🏥', badge: 'bg-blue-100 text-blue-700',     border: 'border-l-blue-400'   },
  { value: 'emergency',   label: 'ER / Emergency',    emoji: '🚨', badge: 'bg-red-100 text-red-700',       border: 'border-l-red-500'    },
  { value: 'dental',      label: 'Dental',            emoji: '🦷', badge: 'bg-cyan-100 text-cyan-700',     border: 'border-l-cyan-400'   },
  { value: 'vision',      label: 'Vision',            emoji: '👁️', badge: 'bg-indigo-100 text-indigo-700', border: 'border-l-indigo-400' },
  { value: 'therapy',     label: 'Therapy',           emoji: '🧠', badge: 'bg-violet-100 text-violet-700', border: 'border-l-violet-400' },
  { value: 'vaccination', label: 'Vaccination',       emoji: '💉', badge: 'bg-green-100 text-green-700',   border: 'border-l-green-400'  },
  { value: 'specialist',  label: 'Specialist',        emoji: '🔬', badge: 'bg-amber-100 text-amber-700',   border: 'border-l-amber-400'  },
  { value: 'medication',  label: 'Medication Change', emoji: '💊', badge: 'bg-orange-100 text-orange-700', border: 'border-l-orange-400' },
  { value: 'denied',      label: 'Access Denied',     emoji: '🚫', badge: 'bg-red-100 text-red-800',       border: 'border-l-red-600'    },
  { value: 'other',       label: 'Other',             emoji: '📋', badge: 'bg-slate-100 text-slate-600',   border: 'border-l-slate-400'  },
]
const RT_MAP = Object.fromEntries(RECORD_TYPES.map(t => [t.value, t]))

// ── Helpers ────────────────────────────────────────────────────────────────────
const HIPAA_SESSION_KEY = 'cg_hipaa_med_ok'

function fmtDate(ymd) {
  if (!ymd) return ''
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  const today = todayYMD()
  const yest = new Date(); yest.setDate(yest.getDate() - 1)
  const yYMD = `${yest.getFullYear()}-${String(yest.getMonth()+1).padStart(2,'0')}-${String(yest.getDate()).padStart(2,'0')}`
  if (ymd === today) return 'Today'
  if (ymd === yYMD)  return 'Yesterday'
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtTs(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

// ── HIPAA consent gate ─────────────────────────────────────────────────────────
function HipaaGate({ onProceed, onBack }) {
  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <AppHeader title="Medical Records" showBack onBack={onBack} />
      <div className="screen no-scrollbar px-4 py-6 flex flex-col gap-5 items-center justify-center">
        <div className="card max-w-sm w-full p-6 flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🔒</span>
            <div>
              <p className="font-bold text-slate-800 dark:text-slate-100">HIPAA Notice</p>
              <p className="text-xs text-slate-500">Protected Health Information</p>
            </div>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed space-y-2">
            <p>This section stores protected health information (PHI) about your children. By proceeding you acknowledge:</p>
            <ul className="list-disc list-inside space-y-1 pl-1 text-slate-500">
              <li>Every record view is logged with a timestamp and your identity.</li>
              <li>Records are encrypted at rest (AES-256) on Cloudflare's infrastructure.</li>
              <li>GoodGuardian is not a HIPAA covered entity and does not replace formal medical record systems.</li>
              <li>Sharing records externally is your responsibility — obtain proper consent.</li>
            </ul>
          </div>
          <div className="disclaimer text-xs">
            ⚠️ For emergencies, call 911 or your child's provider directly.
          </div>
          <div className="flex gap-3 pt-1">
            <Button variant="ghost" size="md" className="flex-1" onClick={onBack}>Back</Button>
            <Button variant="primary" size="md" className="flex-1" onClick={onProceed}>I Understand</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Child health summary card ──────────────────────────────────────────────────
function HealthSummaryCard({ child }) {
  const [expanded, setExpanded] = useState(false)
  if (!child) return null
  const hasInfo = child.allergies || child.medications || child.blood_type ||
                  child.pediatrician_name || child.insurance_provider
  if (!hasInfo) return null

  return (
    <div className="card p-4">
      <button
        className="w-full flex items-center justify-between"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
            {child.name}'s Health Summary
          </span>
        </div>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
          className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 grid grid-cols-2 gap-3">
          {child.blood_type && (
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Blood Type</p>
              <p className="text-sm font-bold text-red-600">{child.blood_type}</p>
            </div>
          )}
          {child.allergies && (
            <div className="col-span-2">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Allergies</p>
              <p className="text-sm text-slate-700 dark:text-slate-300">{child.allergies}</p>
            </div>
          )}
          {child.medications && (
            <div className="col-span-2">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Current Medications</p>
              <p className="text-sm text-slate-700 dark:text-slate-300">{child.medications}</p>
            </div>
          )}
          {child.pediatrician_name && (
            <div className="col-span-2">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Pediatrician</p>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                {child.pediatrician_name}
                {child.pediatrician_phone && <span className="text-slate-400"> · {child.pediatrician_phone}</span>}
              </p>
            </div>
          )}
          {child.insurance_provider && (
            <div className="col-span-2">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Insurance</p>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                {child.insurance_provider}
                {child.insurance_id && <span className="text-slate-400"> · ID: {child.insurance_id}</span>}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Record card ────────────────────────────────────────────────────────────────
function RecordCard({ record, onTap, onEdit, currentUserId }) {
  const rt = RT_MAP[record.record_type] || RT_MAP.other
  const isDenied = record.record_type === 'denied'
  const isOwner = record.created_by === currentUserId

  return (
    <div className={`relative w-full bg-white dark:bg-slate-800 rounded-xl shadow-sm border-l-4 ${rt.border}`}>
      <button
        className="w-full px-4 py-3 text-left active:bg-slate-50 dark:active:bg-slate-700 rounded-xl"
        onClick={() => onTap(record)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <span className="text-base leading-none mt-0.5 shrink-0">{rt.emoji}</span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-snug">
                {isDenied ? record.provider_name || rt.label : (record.provider_name || rt.label)}
              </p>
              {record.diagnosis && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{record.diagnosis}</p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-[10px] text-slate-400">{fmtDate(record.date)}</span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${rt.badge}`}>{rt.label}</span>
          </div>
        </div>
        {(record.treatment || record.notes) && (
          <p className="text-xs text-slate-400 mt-2 line-clamp-1">
            {record.treatment || record.notes}
          </p>
        )}
        {isDenied && (
          <div className="flex items-center gap-1 mt-2">
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
              ⚖️ Court-relevant
            </span>
          </div>
        )}
      </button>
      {isOwner && (
        <button
          onClick={e => { e.stopPropagation(); onEdit(record) }}
          className="absolute top-2.5 right-2.5 tap-target p-1.5 rounded-lg text-slate-400 hover:text-brand-700 hover:bg-brand-100/60 dark:hover:bg-slate-700 transition-colors"
          aria-label="Edit record"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M16.862 3.487a2.25 2.25 0 013.182 3.182L7.5 19.213l-4.5 1.124 1.125-4.5L16.862 3.487z" />
          </svg>
        </button>
      )}
    </div>
  )
}

// ── Edit record modal ──────────────────────────────────────────────────────────
function EditRecordModal({ record, onClose, onSaved }) {
  const rt = RT_MAP[record.record_type] || RT_MAP.other
  const isDenied = record.record_type === 'denied'

  const [form, setForm] = useState({
    record_type:    record.record_type    || '',
    date:           record.date           || '',
    provider_name:  record.provider_name  || '',
    provider_phone: record.provider_phone || '',
    diagnosis:      record.diagnosis      || '',
    treatment:      record.treatment      || '',
    notes:          record.notes          || '',
  })
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState(null)

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))
  const currentIsDenied = form.record_type === 'denied'
  const canSave = form.record_type && form.date

  const providerLabel  = currentIsDenied ? 'Medical matter / provider involved' : 'Provider name'
  const diagnosisLabel = currentIsDenied ? 'What was denied or withheld'        : 'Diagnosis'

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true); setError(null)
    try {
      const res = await api.putMedRecord(record.id, form)
      onSaved(res.record)
    } catch (e) {
      setError(e.message || 'Failed to update record')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex flex-col justify-end z-50">
      <div className="bg-white dark:bg-slate-800 rounded-t-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700 border-l-4 ${rt.border} rounded-tl-2xl`}>
          <div className="flex items-center gap-2">
            <span className="text-xl">{rt.emoji}</span>
            <p className="font-bold text-slate-800 dark:text-slate-100">Edit Record</p>
          </div>
          <button onClick={onClose} className="tap-target text-slate-400 hover:text-slate-600">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto no-scrollbar px-5 py-4 flex flex-col gap-4">

          {/* Record type */}
          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-2">Record Type <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 gap-2">
              {RECORD_TYPES.map(rt2 => (
                <button
                  key={rt2.value}
                  type="button"
                  onClick={() => set('record_type', rt2.value)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium border-2 transition-colors text-left ${
                    form.record_type === rt2.value
                      ? 'border-brand-700 bg-brand-50 text-brand-700 dark:bg-brand-900/20'
                      : 'border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  <span>{rt2.emoji}</span>
                  <span className="text-xs leading-tight">{rt2.label}</span>
                </button>
              ))}
            </div>
          </div>

          {currentIsDenied && (
            <div className="disclaimer text-xs">
              🚫 <strong>Denied access</strong> records are automatically flagged as court-relevant and will appear in PDF exports as evidence of unilateral medical decision-making.
            </div>
          )}

          {/* Date */}
          <Input label="Date *" type="date" value={form.date} onChange={e => set('date', e.target.value)} />

          {/* Provider */}
          <Input
            label={providerLabel}
            value={form.provider_name}
            onChange={e => set('provider_name', e.target.value)}
            placeholder={currentIsDenied ? "e.g. Dr. Smith / Children's Hospital" : 'e.g. Dr. Johnson, Pediatrics'}
          />
          {!currentIsDenied && (
            <Input
              label="Provider phone"
              type="tel"
              value={form.provider_phone}
              onChange={e => set('provider_phone', e.target.value)}
              placeholder="(555) 000-0000"
            />
          )}

          {/* Diagnosis */}
          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">{diagnosisLabel}</label>
            <textarea
              value={form.diagnosis}
              onChange={e => set('diagnosis', e.target.value)}
              rows={3}
              className="input-base resize-none text-sm"
              placeholder={currentIsDenied ? 'Describe what records or decisions were denied…' : 'e.g. Upper respiratory infection'}
            />
          </div>

          {/* Treatment (hidden for denied) */}
          {!currentIsDenied && (
            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">Treatment / Prescription</label>
              <textarea
                value={form.treatment}
                onChange={e => set('treatment', e.target.value)}
                rows={2}
                className="input-base resize-none text-sm"
                placeholder="e.g. Amoxicillin 250mg, 10 days"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">
              {currentIsDenied ? 'Additional context' : 'Notes'}
            </label>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={2}
              className="input-base resize-none text-sm"
              placeholder={currentIsDenied
                ? 'Who denied access? Were witnesses present? What communication exists?'
                : 'Follow-up instructions, referrals, insurance notes…'}
            />
          </div>

          {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
        </div>

        <div className="px-5 pb-2 pt-1">
          <p className="text-[10px] text-slate-400 flex items-center gap-1">
            <span>🔒</span> HIPAA: this edit will be logged in the audit trail.
          </p>
        </div>

        <div className="px-5 pb-6 pt-2 flex gap-3">
          <Button variant="ghost" size="lg" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button
            variant={currentIsDenied ? 'danger' : 'primary'}
            size="lg"
            className="flex-1"
            loading={saving}
            disabled={!canSave}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Record detail modal ────────────────────────────────────────────────────────
function RecordDetailModal({ record, onClose, onDelete }) {
  const [detail,    setDetail]    = useState(null)
  const [loading,   setLoading]   = useState(true)
  const [showAudit, setShowAudit] = useState(false)
  const [deleting,  setDeleting]  = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)

  const rt = RT_MAP[record.record_type] || RT_MAP.other

  useEffect(() => {
    api.getMedRecord(record.id)
      .then(res => setDetail(res))
      .catch(() => setDetail({ record, access_log: [] }))
      .finally(() => setLoading(false))
  }, [record.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.deleteMedRecord(record.id)
      onDelete(record.id)
    } catch {
      setDeleting(false)
      setConfirmDel(false)
    }
  }

  const r = detail?.record || record

  return (
    <div className="fixed inset-0 bg-black/60 flex flex-col justify-end z-50">
      <div className="bg-white dark:bg-slate-800 rounded-t-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700 border-l-4 ${rt.border} rounded-tl-2xl`}>
          <div className="flex items-center gap-2">
            <span className="text-xl">{rt.emoji}</span>
            <div>
              <p className="font-bold text-slate-800 dark:text-slate-100">{rt.label}</p>
              <p className="text-xs text-slate-400">{fmtDate(r.date)}</p>
            </div>
          </div>
          <button onClick={onClose} className="tap-target text-slate-400 hover:text-slate-600">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto no-scrollbar px-5 py-4 flex flex-col gap-4">
          {loading ? (
            <p className="text-center text-slate-400 py-8 text-sm">Loading record…</p>
          ) : (
            <>
              {/* Fields */}
              {r.provider_name && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">
                    {r.record_type === 'denied' ? 'Medical Matter / Provider' : 'Provider'}
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-200">{r.provider_name}</p>
                  {r.provider_phone && <p className="text-xs text-slate-400">{r.provider_phone}</p>}
                </div>
              )}
              {r.diagnosis && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">
                    {r.record_type === 'denied' ? 'What Was Denied / Withheld' : 'Diagnosis'}
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-200">{r.diagnosis}</p>
                </div>
              )}
              {r.treatment && r.record_type !== 'denied' && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Treatment</p>
                  <p className="text-sm text-slate-700 dark:text-slate-200">{r.treatment}</p>
                </div>
              )}
              {r.notes && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Notes</p>
                  <p className="text-sm text-slate-700 dark:text-slate-200">{r.notes}</p>
                </div>
              )}
              {r.created_by_name && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Logged by</p>
                  <p className="text-xs text-slate-500">{r.created_by_name}</p>
                </div>
              )}

              {/* HIPAA audit log */}
              {detail?.access_log?.length > 0 && (
                <div className="border-t border-slate-100 dark:border-slate-700 pt-3">
                  <button
                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 w-full"
                    onClick={() => setShowAudit(a => !a)}
                  >
                    <span>🔒 HIPAA Access Log ({detail.access_log.length})</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
                      className={`w-3.5 h-3.5 transition-transform ${showAudit ? 'rotate-180' : ''}`}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showAudit && (
                    <div className="mt-2 space-y-1.5">
                      {detail.access_log.map((entry, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-slate-50 dark:bg-slate-700 rounded-lg px-3 py-2">
                          <span className="text-slate-600 dark:text-slate-300 font-medium">{entry.name || entry.email}</span>
                          <span className="text-slate-400">{entry.action} · {fmtTs(entry.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Delete */}
              <div className="border-t border-slate-100 dark:border-slate-700 pt-3">
                {!confirmDel ? (
                  <button
                    className="text-xs text-red-500 hover:text-red-700 font-semibold"
                    onClick={() => setConfirmDel(true)}
                  >
                    Delete record…
                  </button>
                ) : (
                  <div className="flex gap-3 items-center">
                    <p className="text-xs text-red-600 font-medium flex-1">Delete this record permanently?</p>
                    <button className="text-xs text-slate-500 font-semibold" onClick={() => setConfirmDel(false)}>Cancel</button>
                    <Button variant="danger" size="sm" loading={deleting} onClick={handleDelete}>Delete</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Add record modal ───────────────────────────────────────────────────────────
const EMPTY_FORM = {
  child_id: '', record_type: '', date: '',
  provider_name: '', provider_phone: '',
  diagnosis: '', treatment: '', notes: '',
}

function AddRecordModal({ onClose, onSave, children, defaultChildId }) {
  const [form,   setForm]   = useState({ ...EMPTY_FORM, date: todayYMD(), child_id: defaultChildId || '' })
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState(null)
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const isDenied  = form.record_type === 'denied'
  const canSave   = form.child_id && form.record_type && form.date

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true); setError(null)
    try {
      const res = await api.postMedRecord(form)
      onSave(res.record)
    } catch (e) {
      setError(e.message || 'Failed to save record')
      setSaving(false)
    }
  }

  const providerLabel   = isDenied ? 'Medical matter / provider involved' : 'Provider name'
  const diagnosisLabel  = isDenied ? 'What was denied or withheld'       : 'Diagnosis'

  return (
    <div className="fixed inset-0 bg-black/60 flex flex-col justify-end z-50">
      <div className="bg-white dark:bg-slate-800 rounded-t-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <p className="font-bold text-slate-800 dark:text-slate-100">Log Medical Record</p>
          <button onClick={onClose} className="tap-target text-slate-400 hover:text-slate-600">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto no-scrollbar px-5 py-4 flex flex-col gap-4">

          {/* Child */}
          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">Child <span className="text-red-500">*</span></label>
            <select
              value={form.child_id}
              onChange={e => set('child_id', e.target.value)}
              className="input-base text-sm"
            >
              <option value="">Select child…</option>
              {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Record type */}
          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-2">Record Type <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 gap-2">
              {RECORD_TYPES.map(rt => (
                <button
                  key={rt.value}
                  type="button"
                  onClick={() => set('record_type', rt.value)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium border-2 transition-colors text-left ${
                    form.record_type === rt.value
                      ? 'border-brand-700 bg-brand-50 text-brand-700 dark:bg-brand-900/20'
                      : 'border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  <span>{rt.emoji}</span>
                  <span className="text-xs leading-tight">{rt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {isDenied && (
            <div className="disclaimer text-xs">
              🚫 <strong>Denied access</strong> records are automatically flagged as court-relevant and will appear in PDF exports as evidence of unilateral medical decision-making.
            </div>
          )}

          {/* Date */}
          <Input label="Date *" type="date" value={form.date} onChange={e => set('date', e.target.value)} />

          {/* Provider */}
          <Input
            label={providerLabel}
            value={form.provider_name}
            onChange={e => set('provider_name', e.target.value)}
            placeholder={isDenied ? "e.g. Dr. Smith / Children's Hospital" : 'e.g. Dr. Johnson, Pediatrics'}
          />
          {!isDenied && (
            <Input
              label="Provider phone"
              type="tel"
              value={form.provider_phone}
              onChange={e => set('provider_phone', e.target.value)}
              placeholder="(555) 000-0000"
            />
          )}

          {/* Diagnosis / denial description */}
          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">{diagnosisLabel}</label>
            <textarea
              value={form.diagnosis}
              onChange={e => set('diagnosis', e.target.value)}
              rows={3}
              className="input-base resize-none text-sm"
              placeholder={isDenied ? 'Describe what records or decisions were denied…' : 'e.g. Upper respiratory infection'}
            />
          </div>

          {/* Treatment (hidden for denied) */}
          {!isDenied && (
            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">Treatment / Prescription</label>
              <textarea
                value={form.treatment}
                onChange={e => set('treatment', e.target.value)}
                rows={2}
                className="input-base resize-none text-sm"
                placeholder="e.g. Amoxicillin 250mg, 10 days"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">
              {isDenied ? 'Additional context' : 'Notes'}
            </label>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={2}
              className="input-base resize-none text-sm"
              placeholder={isDenied
                ? 'Who denied access? Were witnesses present? What communication exists?'
                : 'Follow-up instructions, referrals, insurance notes…'}
            />
          </div>

          {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
        </div>

        <div className="px-5 pb-6 pt-2 flex gap-3">
          <Button variant="ghost" size="lg" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button
            variant={isDenied ? 'danger' : 'primary'}
            size="lg"
            className="flex-1"
            loading={saving}
            disabled={!canSave}
            onClick={handleSave}
          >
            {isDenied ? '⚖️ Log Incident' : 'Save Record'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Main screen ────────────────────────────────────────────────────────────────
export default function MedicalRecords() {
  const { children: kidProfiles, user } = useApp()
  const [hipaaOk,       setHipaaOk]       = useState(() => !!sessionStorage.getItem(HIPAA_SESSION_KEY))
  const [selectedChild, setSelectedChild] = useState(null)
  const [recordsCache,  setRecordsCache]  = useState({})   // { [child_id]: Record[] }
  const [loading,       setLoading]       = useState(false)
  const [typeFilter,    setTypeFilter]    = useState('all')
  const [showAdd,       setShowAdd]       = useState(false)
  const [detailRecord,  setDetailRecord]  = useState(null)
  const [editRecord,    setEditRecord]    = useState(null)

  // Default to first child
  useEffect(() => {
    if (kidProfiles.length > 0 && !selectedChild) {
      setSelectedChild(kidProfiles[0].id)
    }
  }, [kidProfiles]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load records when selected child changes
  useEffect(() => {
    if (!selectedChild || !hipaaOk || recordsCache[selectedChild] !== undefined) return
    setLoading(true)
    api.getMedical(selectedChild)
      .then(res => setRecordsCache(c => ({ ...c, [selectedChild]: res.records || [] })))
      .catch(() => setRecordsCache(c => ({ ...c, [selectedChild]: [] })))
      .finally(() => setLoading(false))
  }, [selectedChild, hipaaOk]) // eslint-disable-line react-hooks/exhaustive-deps

  const currentChild   = kidProfiles.find(c => c.id === selectedChild)
  const allRecords     = recordsCache[selectedChild] || []
  const thisYear       = new Date().getFullYear().toString()

  const filtered = useMemo(() => {
    if (typeFilter === 'all') return allRecords
    return allRecords.filter(r => r.record_type === typeFilter)
  }, [allRecords, typeFilter])

  const grouped = useMemo(() => {
    const map = new Map()
    for (const r of filtered) {
      const d = r.date || 'unknown'
      if (!map.has(d)) map.set(d, [])
      map.get(d).push(r)
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [filtered])

  const stats = useMemo(() => ({
    total:    allRecords.length,
    er:       allRecords.filter(r => r.record_type === 'emergency').length,
    denied:   allRecords.filter(r => r.record_type === 'denied').length,
    thisYear: allRecords.filter(r => r.date?.startsWith(thisYear)).length,
  }), [allRecords, thisYear])

  const handleHipaaProceed = useCallback(() => {
    sessionStorage.setItem(HIPAA_SESSION_KEY, '1')
    setHipaaOk(true)
  }, [])

  const handleSave = useCallback((record) => {
    // Invalidate cache for the child so next load is fresh; optimistically insert
    setRecordsCache(c => ({
      ...c,
      [record.child_id]: [record, ...(c[record.child_id] || [])],
    }))
    setShowAdd(false)
  }, [])

  const handleDelete = useCallback((id) => {
    setDetailRecord(null)
    setRecordsCache(c => ({
      ...c,
      [selectedChild]: (c[selectedChild] || []).filter(r => r.id !== id),
    }))
  }, [selectedChild])

  const handleEditSaved = useCallback((updated) => {
    setEditRecord(null)
    setRecordsCache(c => ({
      ...c,
      [selectedChild]: (c[selectedChild] || []).map(r => r.id === updated.id ? { ...r, ...updated } : r),
    }))
  }, [selectedChild])

  const handleExport = useCallback(() => {
    if (!currentChild || allRecords.length === 0) return
    printMedicalRecords({ child: currentChild, records: allRecords })
  }, [currentChild, allRecords])

  // ── HIPAA gate ───────────────────────────────────────────────────────────────
  if (!hipaaOk) {
    return <HipaaGate onProceed={handleHipaaProceed} onBack={() => history.back()} />
  }

  // ── No children ──────────────────────────────────────────────────────────────
  if (kidProfiles.length === 0) {
    return (
      <div className="flex flex-col h-full bg-white dark:bg-slate-900">
        <AppHeader title="Medical Records" showBack />
        <div className="screen no-scrollbar flex flex-col items-center justify-center gap-3 text-center px-6">
          <span className="text-4xl">👶</span>
          <p className="font-bold text-slate-700 dark:text-slate-200">No children added yet</p>
          <p className="text-sm text-slate-400">Add a child profile first to start tracking medical records.</p>
        </div>
      </div>
    )
  }

  const exportBtn = (
    <button
      onClick={handleExport}
      disabled={allRecords.length === 0}
      className="tap-target text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/20 text-white disabled:opacity-40"
    >
      📄 PDF
    </button>
  )

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <AppHeader title="Medical Records" showBack right={exportBtn} />

      <div className="screen no-scrollbar px-4 py-4 space-y-4">

        {/* Child tabs */}
        {kidProfiles.length > 1 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {kidProfiles.map(child => (
              <button
                key={child.id}
                onClick={() => { setSelectedChild(child.id); setTypeFilter('all') }}
                className={`whitespace-nowrap text-sm font-semibold px-4 py-2 rounded-full transition-colors ${
                  selectedChild === child.id
                    ? 'bg-brand-700 text-white'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                }`}
              >
                {child.name}
                {recordsCache[child.id] && (
                  <span className={`ml-1.5 text-xs ${selectedChild === child.id ? 'text-white/70' : 'text-slate-400'}`}>
                    {recordsCache[child.id].length}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Child health summary */}
        <HealthSummaryCard child={currentChild} />

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Total',     value: stats.total,    color: 'text-brand-700'  },
            { label: 'ER',        value: stats.er,       color: 'text-red-600'    },
            { label: 'Denied',    value: stats.denied,   color: 'text-red-700'    },
            { label: 'This year', value: stats.thisYear, color: 'text-slate-700 dark:text-slate-300' },
          ].map(s => (
            <div key={s.label} className="card py-3 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Access denied court banner */}
        {stats.denied > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3 flex items-center gap-3">
            <span className="text-2xl shrink-0">⚖️</span>
            <div>
              <p className="text-sm font-bold text-red-700 dark:text-red-400">
                {stats.denied} denied access {stats.denied === 1 ? 'incident' : 'incidents'} on record
              </p>
              <p className="text-xs text-red-600/80 dark:text-red-400/70 mt-0.5">
                These records document unilateral medical decision-making. Include them in court filings.
              </p>
            </div>
          </div>
        )}

        {/* Type filter pills */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setTypeFilter('all')}
            className={`whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
              typeFilter === 'all'
                ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
            }`}
          >
            All {allRecords.length > 0 && <span className="ml-1 opacity-60">{allRecords.length}</span>}
          </button>
          {RECORD_TYPES.map(rt => {
            const count = allRecords.filter(r => r.record_type === rt.value).length
            if (count === 0) return null
            return (
              <button
                key={rt.value}
                onClick={() => setTypeFilter(rt.value)}
                className={`whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full transition-colors flex items-center gap-1 ${
                  typeFilter === rt.value
                    ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                }`}
              >
                {rt.emoji} {rt.label}
                <span className="opacity-60">{count}</span>
              </button>
            )
          })}
        </div>

        {/* Records list */}
        {loading ? (
          <div className="card py-10 text-center">
            <p className="text-slate-400 text-sm">Loading records…</p>
          </div>
        ) : grouped.length === 0 ? (
          <div className="card py-12 text-center">
            <p className="text-3xl mb-2">🏥</p>
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No records yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Tap + to log a doctor visit, diagnosis, or denied access incident.
            </p>
          </div>
        ) : (
          grouped.map(([date, records]) => (
            <div key={date}>
              <div className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900 py-1.5 mb-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  {fmtDate(date)}
                  <span className="ml-2 text-[10px] font-normal normal-case text-slate-400">
                    {records.length} {records.length === 1 ? 'record' : 'records'}
                  </span>
                </p>
              </div>
              <div className="space-y-2">
                {records.map(r => (
                  <RecordCard
                    key={r.id}
                    record={r}
                    onTap={setDetailRecord}
                    onEdit={setEditRecord}
                    currentUserId={user?.id}
                  />
                ))}
              </div>
            </div>
          ))
        )}

        <div className="pb-2" />
      </div>

      {/* FAB */}
      <button
        onClick={() => setShowAdd(true)}
        className="fixed bottom-24 right-5 w-14 h-14 brand-gradient rounded-full shadow-lg flex items-center justify-center text-white text-2xl z-30"
        aria-label="Add medical record"
      >
        +
      </button>

      {/* Modals */}
      {showAdd && (
        <AddRecordModal
          onClose={() => setShowAdd(false)}
          onSave={handleSave}
          children={kidProfiles}
          defaultChildId={selectedChild}
        />
      )}
      {detailRecord && (
        <RecordDetailModal
          record={detailRecord}
          onClose={() => setDetailRecord(null)}
          onDelete={handleDelete}
        />
      )}
      {editRecord && (
        <EditRecordModal
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onSaved={handleEditSaved}
        />
      )}
    </div>
  )
}
